import { Truck } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { StatusBadge } from "@/components/status-badge"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { certificateTranslationKey } from "@/lib/certificates"
import { formatNumber, percent } from "@/lib/format"
import { paths } from "@/routes/paths"
import type { Cooperative } from "@/types/domain"

interface CooperativeCardProps {
  cooperative: Cooperative
}

export function CooperativeCard({ cooperative }: CooperativeCardProps) {
  const { t } = useTranslation(["cooperatives", "common"])
  const mt = t("units.mt", { ns: "common" })

  const contracted = cooperative.contractedTonnes ?? 0
  const delivered = cooperative.deliveredTonnes ?? 0
  const deliveredPercent = percent(delivered, contracted)
  const traceabilityComplete =
    (cooperative.successfulTraceability ?? 0) ===
      (cooperative.completedDeliveries ?? 0) &&
    (cooperative.completedDeliveries ?? 0) > 0
  const certifications = cooperative.certifications ?? []
  const openOrders = cooperative.openOrders ?? 0

  return (
    <Link
      to={paths.cooperativeDetail(cooperative.id)}
      className="block focus:outline-none"
    >
      <Card className="h-full transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring">
        <CardContent className="flex h-full flex-col gap-4">
          <div>
            <h3 className="text-base font-semibold tracking-tight">
              {cooperative.name ?? `#${cooperative.id}`}
            </h3>
            {cooperative.region ? (
              <p className="text-sm text-muted-foreground">
                {cooperative.region}
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                {t("card.contracted")}
              </p>
              <p className="text-lg font-semibold tabular-nums">
                {formatNumber(contracted)} {mt}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("card.deliveredOf", {
                  delivered: formatNumber(delivered),
                  unit: mt,
                  percent: formatNumber(deliveredPercent, 0),
                })}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                {t("card.traceability")}
              </p>
              <div className="mt-1">
                <StatusBadge tone={traceabilityComplete ? "success" : "muted"}>
                  {traceabilityComplete
                    ? t("card.complete")
                    : t("card.incomplete")}
                </StatusBadge>
              </div>
            </div>
          </div>

          {certifications.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {certifications.map((cert) => {
                const key = certificateTranslationKey(cert)
                const label = key
                  ? t(key.replace("common:", ""), { ns: "common" })
                  : cert
                return (
                  <Badge
                    key={cert}
                    variant="outline"
                    className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
                  >
                    {label}
                  </Badge>
                )
              })}
            </div>
          ) : null}

          <div className="mt-auto flex items-center text-sm text-muted-foreground">
            <Truck className="mr-1.5 size-4" />
            <span>{t("card.openOrders", { count: openOrders })}</span>
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}
